import logger from "./logger";

// Date formatting utilities for consistent date display across the application

/**
 * Format date to MM/DD/YYYY for North American users
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateUS = (dateInput) => {
  if (!dateInput) return "Not provided";

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    return "Invalid date";
  }
};

/**
 * Format date with time to MM/DD/YYYY HH:MM AM/PM
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date and time string
 */
export const formatDateTimeUS = (dateInput) => {
  if (!dateInput) return "Not provided";

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    logger.warn("Failed to format date time:", error);
    return "Invalid date";
  }
};

/**
 * Format date for display with long month name (e.g., "January 15, 2024")
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateLongUS = (dateInput) => {
  if (!dateInput) return "Not provided";

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (error) {
    logger.warn("Failed to format date:", error);
    return "Invalid date";
  }
};

/**
 * Format a DATE-ONLY value (e.g. a date of birth) with NO timezone shift.
 * A bare "YYYY-MM-DD" parsed via `new Date()` is treated as UTC midnight and
 * then rendered in the viewer's local timezone, which rolls the calendar day
 * backward for anyone west of UTC (a DOB of 1987-05-11 shows as "May 10" in
 * the US/Canada, but "May 11" in Perth). Parsing the Y/M/D components directly
 * preserves the exact calendar date for every viewer. Long form, "May 11, 1987".
 * @param {string|Date} dateInput - "YYYY-MM-DD" (or ISO) string, or a Date
 * @returns {string} Formatted date string
 */
export const formatDateOfBirth = (dateInput) => {
  if (!dateInput) return "Not provided";

  try {
    let y;
    let mo;
    let d;
    if (dateInput instanceof Date) {
      // Use local components so an already-parsed Date isn't re-shifted.
      y = dateInput.getFullYear();
      mo = dateInput.getMonth() + 1;
      d = dateInput.getDate();
    } else {
      const m = String(dateInput).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return "Invalid date";
      [, y, mo, d] = m.map(Number);
    }
    const date = new Date(y, mo - 1, d);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (error) {
    logger.warn("Failed to format date of birth:", error);
    return "Invalid date";
  }
};

/**
 * Format date for short display (e.g., "Jan 15, 2024")
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateShortUS = (dateInput) => {
  if (!dateInput) return "Not provided";

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    logger.warn("Failed to format date:", error);
    return "Invalid date";
  }
};

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateInput) => {
  if (!dateInput) return "Never";

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "Invalid date";

    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;

    // For older dates, show the actual date
    return formatDateShortUS(date);
  } catch (error) {
    logger.warn("Failed to format relative time:", error);
    return "Invalid date";
  }
};
